import React from 'react';
import PropTypes from 'prop-types';

import {Box, Chip, CircularProgress, List, ListItemButton, ListItemText, Stack, Typography} from 'views/components';
import {PeopleOutline, PersonOutline, CalendarToday, AccountCircleOutlined} from 'views/icons';

import {MarkdownEditor} from '../markdown';
import {Translate, translate} from '../Translate';

import {TASK_TYPES} from './constants';

import backend from 'utils/backend';

const TaskDescriptionField = (props) => {
  const {
    taskDescription,
    updateTaskDescription,
    type,
    setType,
    owners,
    setOwners,
    connections,
    setConnections,
    isEditMode,
    task,
  } = props;

  const [clients, setClients] = React.useState([]);
  const [caregivers, setCaregivers] = React.useState([]);
  const [admins, setAdmins] = React.useState([]);
  const [clientsLoading, setClientsLoading] = React.useState(false);
  const [caregiversLoading, setCaregiversLoading] = React.useState(false);
  const [connectionsLoading, setConnectionsLoading] = React.useState(isEditMode && Boolean(task?.connections?.length));
  const [adminsLoading, setAdminsLoading] = React.useState(false);
  const [listVisible, setListVisible] = React.useState(false);
  const [commandQuery, setCommandQuery] = React.useState('');
  const [activeCommand, setActiveCommand] = React.useState('');
  const [trackedCommandStart, setTrackedCommandStart] = React.useState(null);
  const [highlightedOptionIndex, setHighlightedOptionIndex] = React.useState(0);

  const skipCommandInputRef = React.useRef(false);
  const optionRefs = React.useRef([]);

  const queryTimeout = 300;

  const closeCommandMenu = () => {
    setListVisible(false);
    setCommandQuery('');
    setActiveCommand('');
    setTrackedCommandStart(null);
    setHighlightedOptionIndex(0);
  };

  const getInsertedTextForCommand = (cmd, value) => {
    switch (cmd) {
      case 'assign': {
        const selectedAdmin = admins.find((admin) => admin?.userRn === value);
        return `${selectedAdmin?.firstName || ''} ${selectedAdmin?.lastName || ''}`.trim() || selectedAdmin?.userRn || value;
      }
      case 'caregiver': {
        const selectedCaregiver = caregivers.find((caregiver) => caregiver?.caregiverRn === value);
        return selectedCaregiver?.caregiverName || selectedCaregiver?.caregiverRn || value;
      }
      case 'client': {
        const selectedClient = clients.find((client) => client?.clientRn === value);
        return selectedClient?.clientName || selectedClient?.clientRn || value;
      }
      case 'type':
        return translate(TASK_TYPES[value]?.labelKey).toLowerCase();
      default:
        return '';
    }
  };

  const runCommand = (cmd, action, value = null) => {
    const baseDescription = trackedCommandStart === null ?
      taskDescription :
      taskDescription.slice(0, trackedCommandStart);
    const insertedText = action === 'add' ? getInsertedTextForCommand(cmd, value) : '';
    const nextDescription = insertedText ? `${baseDescription}${insertedText}` : baseDescription;

    updateTaskDescription(nextDescription);
    closeCommandMenu();

    switch (cmd) {
      case 'assign':
        if (action === 'add') {
          const selectedAdmin = admins.find((admin) => admin?.userRn === value);
          if (selectedAdmin) {
            setOwners(
                (prev) =>
                  prev.some((admin) => admin?.userRn === selectedAdmin.userRn) ?
                    prev : [...prev, selectedAdmin],
            );
          }
        }
        if (action === 'remove') {
          setOwners((prev) => prev.filter((owner) => owner?.userRn !== value));
        }
        break;
      case 'type':
        if (action === 'add') {
          setType(value);
        }
        if (action === 'remove') {
          setType(null);
        }
        break;
      case 'client':
        if (action === 'add') {
          const selectedClient = clients.find((client) => client?.clientRn === value);

          if (selectedClient) {
            setConnections((prev) =>
              prev.some((connection) => connection?.RN === selectedClient.clientRn) ?
                prev :
                [...prev, {RN: selectedClient.clientRn, name: selectedClient.clientName, type: 'client'}],
            );
          }
        }
        if (action === 'remove') {
          setConnections((prev) => prev.filter((connection) => connection?.RN !== value));
        }
        break;
      case 'caregiver':
        if (action === 'add') {
          const selectedCaregiver = caregivers.find((caregiver) => caregiver?.caregiverRn === value);

          if (selectedCaregiver) {
            setConnections((prev) =>
              prev.some((connection) => connection?.RN === selectedCaregiver.caregiverRn) ?
                prev :
                [...prev, {RN: selectedCaregiver.caregiverRn, name: selectedCaregiver.caregiverName, type: 'caregiver'}],
            );
          }
        }
        if (action === 'remove') {
          setConnections((prev) => prev.filter((connection) => connection?.RN !== value));
        }
        break;
    }
  };

  const handleCommandInput = (event) => {
    const value = event.currentTarget?.value ?? '';

    if (skipCommandInputRef.current) {
      skipCommandInputRef.current = false;
      return;
    }

    const caret = event.currentTarget?.selectionStart ?? value.length;
    const beforeCaret = value.slice(0, caret);
    const isCaretAtEnd = caret === value.length;
    const commandMatch = /(^|\s)([@#$!])([^\n]*)$/.exec(beforeCaret);
    const commandPrefix = commandMatch?.[2];
    const command = {
      '@': 'assign',
      '#': 'caregiver',
      '$': 'client',
      '!': 'type',
    }[commandPrefix];

    if (!isCaretAtEnd || !command) {
      closeCommandMenu();
      return;
    }

    const commandStart = commandMatch.index + commandMatch[1].length;

    setActiveCommand(command);
    setTrackedCommandStart(commandStart);
    setListVisible(true);
    setCommandQuery((commandMatch[3] || '').trimStart().toLowerCase());
  };

  const handleCommandKeyDown = (event) => {
    if (!listVisible) {
      return;
    }

    if (event.key === 'Escape') {
      skipCommandInputRef.current = true;
      event.preventDefault();
      closeCommandMenu();
      return;
    }

    if (activeOptionsLoading || activeOptions.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      skipCommandInputRef.current = true;
      event.preventDefault();
      setHighlightedOptionIndex((prev) => (prev + 1) % activeOptions.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      skipCommandInputRef.current = true;
      event.preventDefault();
      setHighlightedOptionIndex((prev) => (prev - 1 + activeOptions.length) % activeOptions.length);
      return;
    }

    if (event.key === 'Enter' || event.key === 'Tab') {
      const selectedOption = activeOptions[highlightedOptionIndex] || activeOptions[0];

      if (selectedOption) {
        skipCommandInputRef.current = true;
        event.preventDefault();
        selectedOption.onSelect();
      }
    }
  };

  const handleDescriptionBlur = () => {
    closeCommandMenu();
  };

  const filteredTypes = Object.keys(TASK_TYPES)
      .filter((taskType) => taskType !== 'UNKNOWN')
      .filter((name) => !commandQuery || name.toLowerCase().includes(commandQuery));

  const filteredClients = clients.filter((client) => {
    if (!activeCommand) {
      return false;
    }

    const query = commandQuery.toLowerCase();
    const clientName = (client?.clientName || '').toLowerCase();
    const clientRn = (client?.clientRn || '').toLowerCase();

    return clientName.includes(query) || clientRn.includes(query);
  });
  const filteredCaregivers = caregivers.filter((caregiver) => {
    if (!activeCommand) {
      return false;
    }

    const query = commandQuery.toLowerCase();
    const caregiverName = (caregiver?.caregiverName || '').toLowerCase();
    const caregiverRn = (caregiver?.caregiverRn || '').toLowerCase();

    return caregiverName.includes(query) || caregiverRn.includes(query);
  });
  const filteredAdmins = admins.filter((admin) => {
    if (!activeCommand) {
      return false;
    }

    const query = commandQuery.toLowerCase();
    const adminName = `${admin?.firstName || ''} ${admin?.lastName || ''}`.trim().toLowerCase();
    const adminRn = (admin?.userRn || '').toLowerCase();

    return adminName.includes(query) || adminRn.includes(query);
  });

  const activeOptionsLoading = (() => {
    switch (activeCommand) {
      case 'assign':
        return adminsLoading;
      case 'caregiver':
        return caregiversLoading;
      case 'client':
        return clientsLoading;
      case 'type':
        return false;
      default:
        return false;
    }
  })();

  const activeOptions = (() => {
    switch (activeCommand) {
      case 'client':
        return filteredClients.map((client) => ({
          key: client.clientRn,
          onSelect: () => runCommand('client', 'add', client.clientRn),
          primary: (
            <Stack direction='row' gap={1} alignItems='center'>
              <AccountCircleOutlined sx={{fontSize: 15}} />
              <Typography variant='body2'>{client?.clientName || client?.clientRn}</Typography>
            </Stack>
          ),
          secondaryExpression: 'client.ClientDetail.Tasks.Chips.Connections.client',
        }));
      case 'caregiver':
        return filteredCaregivers.map((caregiver) => ({
          key: caregiver.caregiverRn,
          onSelect: () => runCommand('caregiver', 'add', caregiver.caregiverRn),
          primary: (
            <Stack direction='row' gap={1} alignItems='center'>
              <PeopleOutline sx={{fontSize: 15}} />
              <Typography variant='body2'>{caregiver?.caregiverName || caregiver?.caregiverRn}</Typography>
            </Stack>
          ),
          secondaryExpression: 'client.ClientDetail.Tasks.Chips.Connections.caregiver',
        }));
      case 'assign':
        return filteredAdmins.map((admin) => ({
          key: admin.userRn,
          onSelect: () => runCommand('assign', 'add', admin.userRn),
          primary: (
            <Stack direction='row' gap={1} alignItems='center'>
              <PersonOutline sx={{fontSize: 15}} />
              <Typography variant='body2'>{`${admin?.firstName || ''} ${admin?.lastName || ''}`.trim() || admin?.userRn}</Typography>
            </Stack>
          ),
        }));
      case 'type':
        return filteredTypes.map((taskType) => ({
          key: taskType,
          onSelect: () => runCommand('type', 'add', taskType),
          primary: (
            <Stack direction='row' gap={1} alignItems='center'>
              <CalendarToday sx={{fontSize: 15}} />
              <Typography variant='body2'>
                <Translate expression={TASK_TYPES[taskType].labelKey} />
              </Typography>
            </Stack>
          ),
        }));
      default:
        return [];
    }
  })();

  React.useEffect(() => {
    setHighlightedOptionIndex(0);
  }, [activeCommand, commandQuery]);

  React.useEffect(() => {
    optionRefs.current = optionRefs.current.slice(0, activeOptions.length);

    if (!listVisible || activeOptions.length === 0) {
      return;
    }

    setHighlightedOptionIndex((prev) => Math.min(prev, activeOptions.length - 1));
  }, [activeOptions.length, listVisible]);

  React.useEffect(() => {
    if (!listVisible || activeOptions.length === 0) {
      return;
    }

    optionRefs.current[highlightedOptionIndex]?.scrollIntoView({block: 'nearest'});
  }, [activeOptions.length, highlightedOptionIndex, listVisible]);

  React.useEffect(() => {
    if (activeCommand !== 'client') {
      return;
    }

    let mounted = true;
    const timeoutId = setTimeout(async () => {
      setClientsLoading(true);
      try {
        const filter = [
          {
            key: 'status',
            value: [
              'ACTIVE',
              'LEAD',
              'DETAILS_NEEDED',
              'CG_SEARCH',
              'CG_SEARCH_ACTIVE',
              'CG_PROFILE_SENT',
              'MEET_GREET',
              'STALLED',
            ],
          },
        ];

        if (commandQuery.trim()) {
          filter.push({key: 'clientName', value: `${commandQuery.trim()}*`});
        }

        const sortBy = {sortKey: 'createdAt', sortDirection: 'DESC'};
        const result = await backend.client.queryClient(filter, sortBy);
        const uniqueOptions = Array.from(
            new Map((result?.list ?? []).map((client) => [client.clientRn, client])).values(),
        );

        if (mounted) {
          setClients(uniqueOptions);
        }
      } catch (error) {
        console.error('Failed to load clients', error);
      } finally {
        if (mounted) {
          setClientsLoading(false);
        }
      }
    }, queryTimeout);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [activeCommand, commandQuery]);

  React.useEffect(() => {
    if (activeCommand !== 'caregiver') {
      return;
    }

    let mounted = true;
    const timeoutId = setTimeout(async () => {
      setCaregiversLoading(true);
      try {
        const filter = [];

        if (commandQuery.trim()) {
          filter.push({key: 'caregiverName', value: `${commandQuery.trim()}*`});
        }

        const sortBy = {sortKey: 'createdAt', sortDirection: 'DESC'};
        const result = await backend.caregiver.queryCaregiver(filter, sortBy);
        const uniqueOptions = Array.from(
            new Map((result?.list ?? []).map((caregiver) => [caregiver.caregiverRn, caregiver])).values(),
        );

        if (mounted) {
          setCaregivers(uniqueOptions);
        }
      } catch (error) {
        console.error('Failed to load caregivers', error);
      } finally {
        if (mounted) {
          setCaregiversLoading(false);
        }
      }
    }, queryTimeout);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [activeCommand, commandQuery]);

  React.useEffect(() => {
    let mounted = true;

    const loadAdmins = async () => {
      setAdminsLoading(true);
      try {
        const filter = [
          {key: 'status', value: ['ACTIVE']},
          {key: 'role', value: 'ADMIN'},
        ];
        const sortBy = {sortKey: 'createdAt', sortDirection: 'DESC'};
        const result = await backend.user.queryUser(filter, sortBy);
        const uniqueOptions = Array.from(
            new Map((result?.list ?? []).map((admin) => [admin.userRn, admin])).values(),
        );

        if (mounted) {
          setAdmins(uniqueOptions);
        }
      } catch (error) {
        console.error('Failed to load admins', error);
      } finally {
        if (mounted) {
          setAdminsLoading(false);
        }
      }
    };

    loadAdmins();

    return () => {
      mounted = false;
    };
  }, []);

  React.useEffect(() => {
    if (!isEditMode) {
      return;
    }

    let mounted = true;

    const loadConnections = async () => {
      setConnectionsLoading(true);
      const isClientRn = (value) => /^RN:CLIENT-V1-[^:]+$/.test(value);
      const isCaregiverRn = (value) => /^RN:CAREGIVER-V1-[^:]+$/.test(value);
      const rns = Array.from(new Set((task?.connections ?? [])
          .map((connection) => typeof connection === 'string' ? connection : connection?.RN)
          .filter(Boolean)));

      try {
        const loadedConnections = await Promise.all(rns.map(async (connectionRn) => {
          if (isClientRn(connectionRn)) {
            try {
              const result = await backend.client.getClient(connectionRn, false);
              if (result?.clientDetail) {
                return {RN: connectionRn, name: result.clientDetail.clientName, type: 'client'};
              }
            } catch (error) {}

            return {RN: connectionRn, name: connectionRn, type: 'client'};
          }

          if (isCaregiverRn(connectionRn)) {
            try {
              const result = await backend.caregiver.getCaregiver(connectionRn, false);
              if (result?.caregiverDetail) {
                return {RN: connectionRn, name: result.caregiverDetail.caregiverName, type: 'caregiver'};
              }
            } catch (error) {}

            return {RN: connectionRn, name: connectionRn, type: 'caregiver'};
          }

          return null;
        }));

        if (mounted) {
          setConnections(loadedConnections.filter(Boolean));
        }
      } finally {
        if (mounted) {
          setConnectionsLoading(false);
        }
      }
    };

    loadConnections();

    return () => {
      mounted = false;
    };
  }, [isEditMode, setConnections, task]);

  React.useEffect(() => {
    if (!isEditMode || !task?.ownerRns) {
      return;
    }

    let mounted = true;

    const loadOwners = async () => {
      try {
        const loadedOwners = await Promise.all((task.ownerRns || []).map(async (ownerRn) => {
          const result = await backend.user.getUser(ownerRn, true);
          return result?.user ?? null;
        }));

        if (mounted) {
          setOwners(loadedOwners.filter(Boolean));
        }
      } catch (error) {
        console.error('Failed to load admin', error);
      }
    };

    loadOwners();

    return () => {
      mounted = false;
    };
  }, [isEditMode, setOwners, task]);

  return (
    <Box sx={{position: 'relative'}}>
      <Typography variant="body2" sx={{fontWeight: 500}} gutterBottom>
        <Translate expression='client.ClientDetail.Tasks.Type.Common.DescriptionField' />
      </Typography>
      <Box>
        <Box sx={{mb: 1}}>
          <Box sx={{display: 'flex', flexDirection: 'row', gap: 1, flexWrap: 'wrap'}}>
            {
              (Boolean(type) && TASK_TYPES[type]) &&
                <Chip
                  label={
                    <Stack direction='row' gap={1} alignItems='center'>
                      <Typography variant='body2' color='text.secondary' sx={{color: '#2563EB'}}>
                        <Translate expression='client.ClientDetail.Tasks.Chips.Type' />:
                      </Typography>
                      <Typography variant='body2'>
                        <Translate expression={TASK_TYPES[type].labelKey} />
                      </Typography>
                    </Stack>
                  }
                  onDelete={() => runCommand('type', 'remove')}
                  size='small'
                  sx={{
                    'backgroundColor': '#EFF6FF',
                    'border': '1px solid #BFDBFEDE',
                    'px': '9px',
                    'py': '4px',
                    '& .MuiChip-label': {
                      color: '#2563EB',
                    },
                    '& .MuiChip-deleteIcon': {
                      color: '#2563EB',
                    },
                  }}
                />
            }
            {
              owners?.length > 0 &&
                owners.map((owner) => {
                  return (
                    <Chip
                      key={owner.userRn}
                      label={
                        <Stack direction='row' gap={1} alignItems='center'>
                          <PersonOutline sx={{fontSize: 15}} />
                          <Typography variant='body2' color='text.secondary'>
                            <Translate expression='client.ClientDetail.Tasks.Chips.Assignee' />:
                          </Typography>
                          <Typography variant='body2'>{`${owner?.firstName || ''} ${owner?.lastName || ''}`.trim() || owner?.userRn}</Typography>
                        </Stack>
                      }
                      onDelete={() => runCommand('assign', 'remove', owner.userRn)}
                      size='small'
                      sx={{
                        backgroundColor: '#F9F8F6',
                        border: '1px solid #E8E4DE',
                        px: '9px',
                        py: '4px',
                      }}
                    />
                  );
                })
            }
          </Box>
        </Box>
      </Box>
      {
        connections.length > 0 &&
          <Box>
            <Box sx={{mb: 1}}>
              <Typography variant='subtitle2' color='text.secondary'>
                <Translate expression='client.ClientDetail.Tasks.Chips.Connections.Title' />
              </Typography>
              {
                connectionsLoading &&
                  <Box sx={{display: 'flex', justifyContent: 'flex-start', py: 1}}>
                    <CircularProgress size={18} />
                  </Box>
              }
              {
                !connectionsLoading &&
                  <Box sx={{display: 'flex', flexDirection: 'row', gap: 1, flexWrap: 'wrap'}}>
                    {
                      connections.map((connection, index) => (
                        <Chip
                          key={connection?.RN || `${connection?.name}-${index}`}
                          label={
                            <Stack direction='row' gap={1} alignItems='center'>
                              {
                                connection.type === 'client' ?
                                  <AccountCircleOutlined sx={{fontSize: 15}} /> :
                                  <PeopleOutline sx={{fontSize: 15}} />
                              }
                              <Typography variant='body2' color='text.secondary'>
                                <Translate expression={`client.ClientDetail.Tasks.Chips.Connections.${connection.type}`} />:
                              </Typography>
                              <Typography variant='body2'>{connection?.name}</Typography>
                            </Stack>
                          }
                          onDelete={() => runCommand(connection.type, 'remove', connection.RN)}
                          size='small'
                          sx={{
                            backgroundColor: '#F9F8F6',
                            border: '1px solid #E8E4DE',
                            px: '9px',
                            py: '4px',
                          }}
                        />
                      ))
                    }
                  </Box>
              }
            </Box>
          </Box>
      }
      <MarkdownEditor
        value={taskDescription}
        placeholder={translate('client.ClientDetail.Tasks.Type.Common.DescriptionPlaceholder')}
        onChange={updateTaskDescription}
        onKeyDown={handleCommandKeyDown}
        onKeyUp={handleCommandInput}
        onBlur={handleDescriptionBlur}
        autoFocus={false}
        height={140}
      />
      {
        listVisible &&
          <Box
            onMouseDown={(event) => event.preventDefault()}
            sx={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              bottom: 0,
              top: 'auto',
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'background.paper',
              width: '90%',
              maxHeight: 400,
              overflowY: 'scroll',
            }}
          >
            <Typography variant="body2" color='text.secondary' gutterBottom>
              <Translate expression='client.ClientDetail.Tasks.Generic.AddField' />
            </Typography>
            <List dense>
              {
                activeOptionsLoading &&
                  <Box sx={{display: 'flex', justifyContent: 'center', py: 1}}>
                    <CircularProgress size={18} />
                  </Box>
              }
              {
                activeOptions.map((option, index) => (
                  <ListItemButton
                    key={option.key}
                    ref={(node) => {
                      optionRefs.current[index] = node;
                    }}
                    selected={index === highlightedOptionIndex}
                    onMouseEnter={() => setHighlightedOptionIndex(index)}
                    onClick={option.onSelect}
                  >
                    <ListItemText
                      disableTypography
                      primary={option.primary}
                      secondary={
                        option.secondaryExpression ?
                          <Translate expression={option.secondaryExpression} /> :
                          null
                      }
                    />
                  </ListItemButton>
                ))
              }
            </List>
          </Box>
      }
    </Box>
  );
};

TaskDescriptionField.propTypes = {
  taskDescription: PropTypes.string,
  updateTaskDescription: PropTypes.func.isRequired,
  type: PropTypes.string,
  setType: PropTypes.func.isRequired,
  owners: PropTypes.arrayOf(PropTypes.shape({
    userRn: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  })),
  setOwners: PropTypes.func.isRequired,
  connections: PropTypes.arrayOf(PropTypes.shape({
    RN: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.string,
  })),
  setConnections: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  task: PropTypes.object,
};

export default React.memo(TaskDescriptionField);
